import { createSelector } from 'reselect';
import { flow, Predicate, pipe } from 'fp-ts/function';
import { array, nonEmptyArray, option, record, semigroup, string } from 'fp-ts';
import {
    ProductOrderCoProduct,
    OrderItemCoProducts,
    OrderItem,
    ProductOrderItem,
    Product,
} from '@mews-commander/types';
import { AccountingState, OrderItemType } from '@mews-commander/data-access';
import { getStringProp } from '@mews/fp';
import { divideAmount, isZeroAmount, sumAllAmounts } from '@mews-commander/utils';
import { getOrderItemsByServiceOrderIds, getOrderItems } from '../ducks/orderItems';
import { getServiceDefaultProductId, getProducts } from '../ducks/products';
import { getCurrentEnterpriseDefaultCurrencyCode } from './session/enterprise';
import { getCurrentEnterpriseReservations } from './reservations';

const createStringRecordWithMap = record.fromFoldableMap(semigroup.first<string>(), record.getFoldable(string.Ord));
const getServiceIdToDefaultProductIdMap = createSelector(getProducts, (products) =>
    createStringRecordWithMap(record.filter((p: Product) => p.isDefault)(products), (p) => [p.serviceId, p.id])
);
const getReservationIdToServiceIdMap = createSelector(
    getCurrentEnterpriseReservations,
    record.map((r) => r.serviceId)
);
export const getDefaultProductOrderItemMapByServiceOrderId = createSelector(
    getOrderItems,
    getServiceIdToDefaultProductIdMap,
    getReservationIdToServiceIdMap,
    (orderItems, serviceIdToDefaultProductIdMap, serviceOrderIdToServiceIdMap) =>
        pipe(
            orderItems,
            Object.values<OrderItem>,
            array.filter((orderItem) => {
                const serviceId = serviceOrderIdToServiceIdMap[orderItem.serviceOrderId];
                const defaultProductOrderId = serviceId ? serviceIdToDefaultProductIdMap[serviceId] : null;
                return (
                    isProductOrderItem(orderItem.data) &&
                    !orderItem.isCanceled &&
                    orderItem.data.value.productId === defaultProductOrderId
                );
            }),
            nonEmptyArray.groupBy((orderItem) => orderItem.serviceOrderId)
        )
);
export const getServiceOrdersTimeUnitCount = createSelector(
    getDefaultProductOrderItemMapByServiceOrderId,
    record.map((orderItems) => orderItems.length)
);
export const getServiceOrderTimeUnitCount = createSelector(
    getServiceOrdersTimeUnitCount,
    getStringProp('serviceOrderId'),
    (map, serviceOrderId) => map[serviceOrderId]
);
export const isProductOrderItem = (orderItemData: OrderItemCoProducts): orderItemData is ProductOrderCoProduct =>
    orderItemData.discriminator === OrderItemType.ProductOrder;

export const isTaxCorrectionItem = (orderItemData: OrderItemCoProducts): orderItemData is ProductOrderCoProduct =>
    orderItemData.discriminator === OrderItemType.TaxCorrection;

const isOrderItemAccountable = ({ accountingState, totalAmount }: OrderItem) =>
    (accountingState === AccountingState.Open || accountingState === AccountingState.Closed) &&
    !isZeroAmount(totalAmount);
const isOrderItemInactive = ({ accountingState }: OrderItem) => accountingState === AccountingState.Inactive;

const getReservationsOrderItemsFactory = (
    orderItemPredicate: Predicate<{
        orderItem: OrderItem;
        defaultProductId: string | undefined;
    }>
) =>
    createSelector(getOrderItemsByServiceOrderIds, getServiceDefaultProductId, (orderItems, defaultProductId) => {
        const productOrderItems = orderItems.filter(
            (orderItem) => orderItemPredicate({ orderItem, defaultProductId }) && isProductOrderItem(orderItem.data)
        );
        return productOrderItems as Array<ProductOrderItem>;
    });

const getReservationsProductOrderOrderItems = getReservationsOrderItemsFactory(
    ({ orderItem }) => !orderItem.isCanceled
);

export const getReservationsDefaultProductOrderItems = getReservationsOrderItemsFactory(
    ({ orderItem, defaultProductId }) =>
        isProductOrderItem(orderItem.data) &&
        !orderItem.isCanceled &&
        orderItem.data.value.productId === defaultProductId
);

export const getReservationsNonDefaultProductOrderItems = getReservationsOrderItemsFactory(
    ({ orderItem, defaultProductId }) =>
        isProductOrderItem(orderItem.data) &&
        !orderItem.isCanceled &&
        orderItem.data.value.productId !== defaultProductId
);

export const getReservationsNonDefaultNonDeletedProductOrderItems = createSelector(
    getReservationsNonDefaultProductOrderItems,
    array.filter(({ isDeleted }) => !isDeleted)
);

export const getReservationsAllNonDefaultProductOrderItems = getReservationsOrderItemsFactory(
    ({ orderItem, defaultProductId }) =>
        isProductOrderItem(orderItem.data) && orderItem.data.value.productId !== defaultProductId
);

export const getReservationsAllDefaultProductOrderItems = getReservationsOrderItemsFactory(
    ({ orderItem, defaultProductId }) =>
        isProductOrderItem(orderItem.data) && orderItem.data.value.productId === defaultProductId
);

export const getReservationsAdditionalExpenses = flow(
    getOrderItemsByServiceOrderIds,
    array.filter((orderItem) => !isProductOrderItem(orderItem.data) && !isTaxCorrectionItem(orderItem.data))
);

export const getOrderItemsTotalAmount = (orderItems: OrderItem[], defaultCurrencyCode: string) =>
    sumAllAmounts(
        orderItems.map(({ totalAmount }) => totalAmount),
        defaultCurrencyCode
    );

export const getOrderItemsFullTimeUnitTotalAmount = (orderItems: OrderItem[], defaultCurrencyCode: string) =>
    sumAllAmounts(
        orderItems.map(({ fullTimeUnitTotalAmount }) => fullTimeUnitTotalAmount),
        defaultCurrencyCode
    );

export const getReservationsProductOrderOrderItemsAverageAmount = createSelector(
    getReservationsDefaultProductOrderItems,
    getReservationsProductOrderOrderItems,
    getCurrentEnterpriseDefaultCurrencyCode,
    (defaultOrderItems, productOrderOrderItems, defaultCurrencyCode: string) => {
        const defaultOrderItemsCount = array.size(defaultOrderItems);
        const totalAmount = getOrderItemsFullTimeUnitTotalAmount(productOrderOrderItems, defaultCurrencyCode);

        return defaultOrderItemsCount !== 0 ? divideAmount(totalAmount, defaultOrderItemsCount) : null;
    }
);

const getAccountableOrInactiveOrderItems = createSelector(
    getOrderItemsByServiceOrderIds,
    array.filter((orderItem) => isOrderItemAccountable(orderItem) || isOrderItemInactive(orderItem))
);
export const getReservationsTotalAmount = createSelector(
    getAccountableOrInactiveOrderItems,
    getCurrentEnterpriseDefaultCurrencyCode,
    getOrderItemsTotalAmount
);
export const getReservationsOrderItemsBillIds = createSelector(
    getOrderItemsByServiceOrderIds,
    flow(
        array.filter((orderItem) => orderItem.billId !== orderItem.ownerId),
        array.filterMap((orderItem: OrderItem) => option.fromNullable(orderItem.billId)),
        array.uniq(string.Eq)
    )
);
